import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import { IAmenity, AmenityCategory } from '../amenities/amenities-slice';

const REACT_APP_API_URL = `${process.env.REACT_APP_API_URL}`;

export interface ICompanyAmenity {
  id: number;
  companyId: number;
  amenityId: number;
  customDisplayName?: string;
  customDescription?: string;
  isHighlighted: boolean;
  createdAt: string;
  updatedAt: string;
  amenity?: IAmenity;
}

export interface ICompanyAmenitiesState {
  companyAmenities: ICompanyAmenity[];
  availableAmenities: IAmenity[];
  loading: boolean;
  error?: string;
}

const initialState: ICompanyAmenitiesState = {
  companyAmenities: [],
  availableAmenities: [],
  loading: false,
  error: undefined,
};

interface IQueryParams {
  isHighlighted?: boolean;
  category?: AmenityCategory;
}

// Async Thunks
export const fetchMyCompanyAmenities = createAsyncThunk(
  'companyAmenities/fetchMy',
  async (params?: IQueryParams) => {
    const res = await axios.get<ICompanyAmenity[]>(`${REACT_APP_API_URL}/company-amenities/my`, { params });
    return res.data;
  }
);

export const fetchAvailableAmenities = createAsyncThunk(
  'companyAmenities/fetchAvailable',
  async (params?: IQueryParams) => {
    
    const res = await axios.get<IAmenity[]>(`${REACT_APP_API_URL}/company-amenities/available`, { params });
    debugger;
    return res.data;
  }
);

export const addCompanyAmenity = createAsyncThunk(
  'companyAmenities/add',
  async (data: {
    amenityId: number;
    customDisplayName?: string;
    customDescription?: string;
    isHighlighted?: boolean;
  }) => {
    const res = await axios.post<ICompanyAmenity>(`${REACT_APP_API_URL}/company-amenities/my`, data);
    return res.data;
  }
);

export const updateCompanyAmenity = createAsyncThunk(
  'companyAmenities/update',
  async ({ amenityId, data }: {
    amenityId: number;
    data: {
      customDisplayName?: string;
      customDescription?: string;
      isHighlighted?: boolean;
    };
  }) => {
    const res = await axios.put<ICompanyAmenity>(
      `${REACT_APP_API_URL}/company-amenities/my/${amenityId}`,
      data
    );
    return res.data;
  }
);

export const deleteCompanyAmenity = createAsyncThunk(
  'companyAmenities/delete',
  async (amenityId: number) => {
    await axios.delete(`${REACT_APP_API_URL}/company-amenities/my/${amenityId}`);
    return amenityId;
  }
);

export const syncCompanyAmenities = createAsyncThunk(
  'companyAmenities/sync',
  async (amenityIds: number[]) => {
    const res = await axios.put<ICompanyAmenity[]>(
      `${REACT_APP_API_URL}/company-amenities/my/sync`,
      { amenityIds }
    );
    return res.data;
  }
);

export const batchAddCompanyAmenities = createAsyncThunk(
  'companyAmenities/batchAdd',
  async (amenities: Array<{
    amenityId: number;
    customDisplayName?: string;
    customDescription?: string;
    isHighlighted?: boolean;
  }>) => {
    const promises = amenities.map((amenity) =>
      axios.post<ICompanyAmenity>(`${REACT_APP_API_URL}/company-amenities/my`, amenity)
    );
    const results = await Promise.all(promises);
    return results.map((res) => res.data);
  }
);

const companyAmenitiesSlice = createSlice({
  name: 'companyAmenities',
  initialState,
  reducers: {
    clearCompanyAmenities: (state) => {
      state.companyAmenities = [];
      state.availableAmenities = [];
      state.loading = false;
      state.error = undefined;
    },
    addLocalCompanyAmenity: (state, action: PayloadAction<ICompanyAmenity>) => {
      state.companyAmenities.push(action.payload);
    },
    removeLocalCompanyAmenity: (state, action: PayloadAction<number>) => {
      state.companyAmenities = state.companyAmenities.filter((ca) => ca.amenityId !== action.payload);
    },
    updateLocalCompanyAmenity: (state, action: PayloadAction<ICompanyAmenity>) => {
      const index = state.companyAmenities.findIndex((ca) => ca.id === action.payload.id);
      if (index !== -1) {
        state.companyAmenities[index] = action.payload;
      }
    },
  },
  extraReducers: (builder) => {
    // Fetch My Company Amenities
    builder.addCase(fetchMyCompanyAmenities.pending, (state) => {
      state.loading = true;
      state.error = undefined;
    });
    builder.addCase(fetchMyCompanyAmenities.fulfilled, (state, action) => {
      state.loading = false;
      state.companyAmenities = action.payload;
    });
    builder.addCase(fetchMyCompanyAmenities.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message;
    });

    // Fetch Available Amenities
    builder.addCase(fetchAvailableAmenities.pending, (state) => {
      state.loading = true;
      state.error = undefined;
    });
    builder.addCase(fetchAvailableAmenities.fulfilled, (state, action) => {
      state.loading = false;
      state.availableAmenities = action.payload;
    });
    builder.addCase(fetchAvailableAmenities.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message;
    });

    // Add Company Amenity
    builder.addCase(addCompanyAmenity.pending, (state) => {
      state.loading = true;
      state.error = undefined;
    });
    builder.addCase(addCompanyAmenity.fulfilled, (state, action) => {
      state.loading = false;
      state.companyAmenities.push(action.payload);
    });
    builder.addCase(addCompanyAmenity.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message;
    });

    // Update Company Amenity
    builder.addCase(updateCompanyAmenity.pending, (state) => {
      state.loading = true;
      state.error = undefined;
    });
    builder.addCase(updateCompanyAmenity.fulfilled, (state, action) => {
      state.loading = false;
      const index = state.companyAmenities.findIndex((ca) => ca.id === action.payload.id);
      if (index !== -1) {
        state.companyAmenities[index] = action.payload;
      }
    });
    builder.addCase(updateCompanyAmenity.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message;
    });

    // Delete Company Amenity
    builder.addCase(deleteCompanyAmenity.pending, (state) => {
      state.loading = true;
      state.error = undefined;
    });
    builder.addCase(deleteCompanyAmenity.fulfilled, (state, action) => {
      state.loading = false;
      state.companyAmenities = state.companyAmenities.filter((ca) => ca.amenityId !== action.payload);
    });
    builder.addCase(deleteCompanyAmenity.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message;
    });

    // Sync Company Amenities
    builder.addCase(syncCompanyAmenities.pending, (state) => {
      state.loading = true;
      state.error = undefined;
    });
    builder.addCase(syncCompanyAmenities.fulfilled, (state, action) => {
      state.loading = false;
      state.companyAmenities = action.payload;
    });
    builder.addCase(syncCompanyAmenities.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message;
    });

    // Batch Add Company Amenities
    builder.addCase(batchAddCompanyAmenities.pending, (state) => {
      state.loading = true;
      state.error = undefined;
    });
    builder.addCase(batchAddCompanyAmenities.fulfilled, (state, action) => {
      state.loading = false;
      state.companyAmenities = [...state.companyAmenities, ...action.payload];
    });
    builder.addCase(batchAddCompanyAmenities.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message;
    });
  },
});

export const {
  clearCompanyAmenities,
  addLocalCompanyAmenity,
  removeLocalCompanyAmenity,
  updateLocalCompanyAmenity,
} = companyAmenitiesSlice.actions;

export default companyAmenitiesSlice.reducer;
